/* Emberwall theme: product page. Variant switching without reloads, gallery counter, sticky bar, specs units, before/after. */
(() => {
  const $ = (root, sel) => root.querySelector(sel);
  const $$ = (root, sel) => Array.from(root.querySelectorAll(sel));

  class EwProduct extends HTMLElement {
    connectedCallback() {
      const json = $(this, '[data-product-json]');
      if (!json) return;
      this.data = JSON.parse(json.textContent);
      this.form = $(this, 'form.ew-product-form');
      this.addEventListener('change', (e) => { if (e.target.matches('[data-option-input]')) this.onOptionChange(); });
      document.addEventListener('ew:select-variant', (e) => this.selectVariant(e.detail.variantId));
      this.current = this.findById(parseInt($(this, '[data-variant-id]').value, 10));
      this.updateStock(this.current);
      this.setupGallery();
      this.setupVideos();
      this.setupLightbox();
      this.setupStickyBar();
    }

    findById(id) { return this.data.variants.find((v) => v.id === id); }

    selectedOptions() {
      return $$(this, '.pdp-option').map((fs) => {
        const checked = fs.querySelector('[data-option-input]:checked');
        return checked ? checked.value : null;
      });
    }

    onOptionChange() {
      const chosen = this.selectedOptions();
      const variant = this.data.variants.find((v) => v.options.every((o, i) => chosen[i] === null || o === chosen[i]));
      this.update(variant || null);
    }

    selectVariant(id) {
      const v = this.findById(id);
      if (!v) return;
      v.options.forEach((value, i) => {
        const input = $$(this, `.pdp-option[data-option-index="${i}"] [data-option-input]`).find((el) => el.value === value);
        if (input) input.checked = true;
      });
      this.update(v);
    }

    setText(sel, text) { $$(this, sel).forEach((el) => { el.textContent = text; }); }

    update(v) {
      const addButtons = $$(this, '[data-add-button]');
      if (!v) {
        addButtons.forEach((b) => { b.disabled = true; });
        this.setText('[data-add-label]', 'Unavailable');
        return;
      }
      this.current = v;
      $$(this, '[data-variant-id]').forEach((input) => { input.value = v.id; });
      this.setText('[data-price]', v.price);
      this.setText('[data-sticky-price]', v.price);
      this.setText('[data-klarna]', v.klarna);
      this.setText('[data-sticky-size]', v.label || v.options[v.options.length - 1]);
      $$(this, '[data-compare]').forEach((el) => { el.hidden = !v.compare; el.textContent = v.compare || ''; });
      $$(this, '[data-save]').forEach((el) => { el.hidden = !v.save; });
      $$(this, '[data-promo-label], [data-promo-badge]').forEach((el) => { el.hidden = !v.compare; });
      this.setText('[data-save-pct]', v.save);
      $$(this, '[data-cutout-line]').forEach((el) => { el.hidden = !v.cutout; });
      if (v.cutout) this.setText('[data-cutout]', v.cutout);
      addButtons.forEach((b) => { b.disabled = !v.available; });
      this.setText('[data-add-label]', v.available ? (this.dataset.addLabel || 'Add to basket') : 'Sold out');
      this.updateStock(v);
      $$(this, '.pdp-option').forEach((fs) => {
        const checked = fs.querySelector('[data-option-input]:checked');
        const label = fs.querySelector('[data-selected-value]');
        if (checked && label) label.textContent = checked.dataset.label || checked.value;
      });
      if (v.media) this.scrollToMedia(v.media);
      const url = new URL(window.location.href);
      url.searchParams.set('variant', v.id);
      window.history.replaceState({}, '', url.toString());
      document.dispatchEvent(new CustomEvent('ew:variant-change', { detail: { variantId: v.id } }));
    }

    updateStock(v) {
      const el = $(this, '[data-low-stock]');
      if (!el || !v) return;
      const threshold = parseInt(el.dataset.threshold, 10);
      const show = v.stock !== null && v.stock > 0 && v.stock <= threshold;
      el.hidden = !show;
      if (show) {
        const text = el.dataset.template.replace('[count]', v.stock).replace('[size]', v.label || v.options[v.options.length - 1]);
        $(el, '[data-low-stock-text]').textContent = text;
      }
    }

    setupGallery() {
      this.gallery = $(this, '[data-gallery]');
      const counter = $(this, '[data-gallery-index]');
      if (!this.gallery || !counter || !('IntersectionObserver' in window)) return;
      const items = $$(this.gallery, '.pdp-gallery__item');
      const thumbs = $$(this, '[data-thumb]');
      thumbs.forEach((t) => t.addEventListener('click', () => {
        const target = items[parseInt(t.dataset.thumb, 10)];
        if (target) this.gallery.scrollTo({ left: target.offsetLeft - this.gallery.offsetLeft, behavior: 'smooth' });
      }));
      const io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting || entry.intersectionRatio <= 0.6) return;
          const idx = items.indexOf(entry.target);
          counter.textContent = idx + 1;
          thumbs.forEach((t, k) => t.classList.toggle('is-active', k === idx));
        });
      }, { root: this.gallery, threshold: [0.6] });
      items.forEach((item) => io.observe(item));
      const step = (dir) => {
        const i = Math.round(this.gallery.scrollLeft / this.gallery.clientWidth) + dir;
        const n = (i + items.length) % items.length;
        this.gallery.scrollTo({ left: items[n].offsetLeft - this.gallery.offsetLeft, behavior: 'smooth' });
      };
      const prev = $(this, '[data-gallery-prev]');
      const next = $(this, '[data-gallery-next]');
      if (prev) prev.addEventListener('click', () => step(-1));
      if (next) next.addEventListener('click', () => step(1));
    }

    scrollToMedia(mediaId) {
      if (!this.gallery || (window.matchMedia('(min-width: 990px)').matches && !this.querySelector('.pdp-gallery--slider'))) return;
      const item = $(this.gallery, `[data-media-id="${mediaId}"]`);
      if (item) this.gallery.scrollTo({ left: item.offsetLeft, behavior: 'smooth' });
    }

    setupVideos() {
      const videos = $$(this, '.pdp-gallery__video');
      if (!videos.length || !('IntersectionObserver' in window)) return;
      const io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const v = entry.target;
          if (entry.isIntersecting && entry.intersectionRatio > 0.5) { v.muted = true; v.play().catch(() => {}); } else { v.pause(); }
        });
      }, { threshold: [0, 0.5, 1] });
      videos.forEach((v) => io.observe(v));
    }

    /* Two-finger zoom and pan on phones, double tap to zoom in or out */
    setupPinch(lb) {
      const dist = (t) => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);
      $$(lb, '.lightbox__slide img').forEach((img) => {
        let s = 1; let x = 0; let y = 0; let start = null; let pan = null; let lastTap = 0;
        const apply = () => {
          img.style.transform = s > 1 ? `translate(${x}px, ${y}px) scale(${s})` : '';
          img.classList.toggle('is-pinched', s > 1);
        };
        const clamp = () => {
          const maxX = (img.clientWidth * (s - 1)) / 2;
          const maxY = (img.clientHeight * (s - 1)) / 2;
          x = Math.max(-maxX, Math.min(maxX, x));
          y = Math.max(-maxY, Math.min(maxY, y));
        };
        img.addEventListener('touchstart', (e) => {
          if (e.touches.length === 2) { start = { d: dist(e.touches), s }; pan = null; }
          else if (e.touches.length === 1) {
            const now = Date.now();
            if (now - lastTap < 280) { s = s > 1 ? 1 : 2.5; x = 0; y = 0; apply(); lastTap = 0; return; }
            lastTap = now;
            if (s > 1) pan = { x: e.touches[0].clientX - x, y: e.touches[0].clientY - y };
          }
        }, { passive: true });
        img.addEventListener('touchmove', (e) => {
          if (e.touches.length === 2 && start) {
            e.preventDefault();
            s = Math.max(1, Math.min(4, start.s * (dist(e.touches) / start.d)));
            clamp(); apply();
          } else if (e.touches.length === 1 && pan && s > 1) {
            e.preventDefault();
            x = e.touches[0].clientX - pan.x; y = e.touches[0].clientY - pan.y;
            clamp(); apply();
          }
        }, { passive: false });
        img.addEventListener('touchend', () => {
          start = null; pan = null;
          if (s < 1.05) { s = 1; x = 0; y = 0; apply(); }
        });
        lb.addEventListener('close', () => { s = 1; x = 0; y = 0; apply(); });
      });
    }

    setupLightbox() {
      const lb = $(this, '[data-lightbox]');
      if (!lb || !window.EwDialog) return;
      window.EwDialog.wire(lb);
      this.setupPinch(lb);
      const track = $(lb, '[data-lb-track]');
      const slides = $$(track, '.lightbox__slide');
      const counter = $(lb, '[data-lb-index]');
      const vertical = () => window.matchMedia('(max-width: 749px)').matches;
      const index = () => Math.round(track.scrollLeft / track.clientWidth);
      const pauseAll = () => $$(lb, 'video').forEach((v) => v.pause());
      const go = (i, smooth = true) => {
        const n = (i + slides.length) % slides.length;
        pauseAll();
        $$(lb, '.lightbox__slide.is-zoomed').forEach((s) => s.classList.remove('is-zoomed'));
        lb.classList.remove('has-zoom');
        if (vertical()) { track.scrollTo({ top: slides[n].offsetTop, behavior: smooth ? 'smooth' : 'auto' }); return; }
        track.scrollTo({ left: n * track.clientWidth, behavior: smooth ? 'smooth' : 'auto' });
      };
      track.addEventListener('scroll', () => { counter.textContent = index() + 1; }, { passive: true });
      $(lb, '[data-lb-prev]').addEventListener('click', () => go(index() - 1));
      $(lb, '[data-lb-next]').addEventListener('click', () => go(index() + 1));
      lb.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') { e.preventDefault(); go(index() + 1); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); go(index() - 1); }
      });
      lb.addEventListener('close', () => { pauseAll(); unzoom(); });
      const unzoom = () => { $$(lb, '.lightbox__slide.is-zoomed').forEach((s) => s.classList.remove('is-zoomed')); lb.classList.remove('has-zoom'); };
      track.addEventListener('click', (e) => {
        const img = e.target.closest('.lightbox__slide img');
        if (!img) return;
        const slide = img.closest('.lightbox__slide');
        const zoomed = slide.classList.toggle('is-zoomed');
        lb.classList.toggle('has-zoom', zoomed);
        if (zoomed) {
          const r = img.getBoundingClientRect();
          const fx = (e.clientX - r.left) / r.width;
          const fy = (e.clientY - r.top) / r.height;
          requestAnimationFrame(() => {
            slide.scrollLeft = fx * slide.scrollWidth - slide.clientWidth / 2;
            slide.scrollTop = fy * slide.scrollHeight - slide.clientHeight / 2;
          });
        }
      });
      this.addEventListener('click', (e) => {
        const opener = e.target.closest('[data-lb-open]');
        if (!opener) return;
        const i = parseInt(opener.dataset.lbOpen, 10);
        window.EwDialog.open(lb, opener);
        requestAnimationFrame(() => {
          go(i, false);
          counter.textContent = i + 1;
          if (opener.hasAttribute('data-lb-play')) {
            const video = slides[i] && slides[i].querySelector('video');
            if (video) video.play().catch(() => {});
          }
        });
      });
    }

    setupStickyBar() {
      const bar = $(this, '[data-sticky-bar]');
      const buy = $(this, '[data-buy-area]');
      if (!bar || !buy || !('IntersectionObserver' in window)) return;
      // On desktop the bar can also stand in for a button that is still below the first screen.
      const desktop = window.matchMedia('(min-width: 990px)');
      const early = () => desktop.matches && bar.classList.contains('pdp-sticky--desktop');
      const io = new IntersectionObserver(([entry]) => {
        bar.hidden = entry.isIntersecting || (entry.boundingClientRect.top > 0 && !early());
      });
      io.observe(buy);
    }
  }
  if (!customElements.get('ew-product')) customElements.define('ew-product', EwProduct);

  /* Size and specs: follows the selected size, mm or inches */
  class EwSpecs extends HTMLElement {
    connectedCallback() {
      const json = $(this, '[data-specs-json]');
      this.dims = json ? JSON.parse(json.textContent) : {};
      this.unit = 'mm';
      $$(this, '[data-unit]').forEach((btn) => btn.addEventListener('click', () => this.setUnit(btn.dataset.unit)));
      this.onVariant = (e) => this.show(e.detail.variantId);
      $$(this, '[data-pick-size]').forEach((btn) => btn.addEventListener('click', () => {
        const inputs = document.querySelectorAll('.pdp-option:last-of-type [data-option-input]');
        const input = Array.from(inputs).find((el) => el.value === btn.dataset.pickSize);
        if (input && !input.checked) input.click();
      }));
      document.addEventListener('ew:variant-change', this.onVariant);
    }
    disconnectedCallback() { document.removeEventListener('ew:variant-change', this.onVariant); }
    format(mm) {
      if (mm === null || mm === undefined) return '';
      if (this.unit === 'in') return `${(mm / 25.4).toFixed(1)} in`;
      return `${Math.round(mm).toLocaleString('en-GB')} mm`;
    }
    setUnit(unit) {
      this.unit = unit;
      $$(this, '[data-unit]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.unit === unit)));
      $$(this, '[data-mm]').forEach((el) => { el.textContent = this.format(parseFloat(el.dataset.mm)); });
    }
    show(id) {
      const d = this.dims[id];
      if (!d) return;
      Object.keys(d).forEach((key) => {
        $$(this, `[data-dim="${key}"]`).forEach((el) => {
          const val = d[key];
          const wrap = el.closest('[data-dim-wrap]');
          if (wrap) wrap.style.display = val === null ? 'none' : '';
          if (val === null) return;
          if (el.hasAttribute('data-mm')) { el.dataset.mm = val; el.textContent = this.format(val); } else { el.textContent = val; }
        });
      });
      $$(this, '[data-col-size]').forEach((cell) => cell.classList.toggle('is-current', cell.dataset.colSize === d.key));
      this.setText('[data-specs-size]', d.size);
    }
    setText(sel, text) { $$(this, sel).forEach((el) => { el.textContent = text; }); }
  }
  if (!customElements.get('ew-specs')) customElements.define('ew-specs', EwSpecs);

  /* Before / after slider */
  class EwCompare extends HTMLElement {
    connectedCallback() {
      const range = $(this, 'input[type="range"]');
      if (!range) return;
      const set = () => this.style.setProperty('--pos', `${range.value}%`);
      range.addEventListener('input', set);
      set();
    }
  }
  if (!customElements.get('ew-compare')) customElements.define('ew-compare', EwCompare);
})();
/* Product gallery: fast, smooth photo changes and a thumbnail strip that follows the photo on screen.
   - Thumbnails and arrows glide to the photo in about 0.25 s (the browser's own smooth scroll is slow with scroll snapping).
   - Every gallery photo is loaded once the page is ready, so swiping never waits for an image. */
(() => {
  const ease = (p) => 1 - Math.pow(1 - p, 3);
  const glide = (list, left) => {
    cancelAnimationFrame(list._glide || 0);
    const start = list.scrollLeft;
    const distance = left - start;
    if (Math.abs(distance) < 1) return;
    const duration = Math.min(320, 200 + (Math.abs(distance) / list.clientWidth) * 40);
    const t0 = performance.now();
    list.style.scrollSnapType = 'none';
    const frame = (now) => {
      const p = Math.min(1, (now - t0) / duration);
      list.scrollLeft = start + distance * ease(p);
      if (p < 1) list._glide = requestAnimationFrame(frame);
      else list.style.scrollSnapType = '';
    };
    list._glide = requestAnimationFrame(frame);
  };
  const goTo = (gallery, index) => {
    const list = gallery.querySelector('[data-gallery]');
    const items = list ? [...list.querySelectorAll('.pdp-gallery__item')] : [];
    if (!items.length) return;
    const n = (index + items.length) % items.length;
    glide(list, items[n].offsetLeft - items[0].offsetLeft);
  };
  const current = (gallery) => {
    const list = gallery.querySelector('[data-gallery]');
    return list ? Math.round(list.scrollLeft / list.clientWidth) : 0;
  };

  document.addEventListener('click', (e) => {
    const gallery = e.target.closest('.pdp-gallery');
    if (!gallery) return;
    const thumb = e.target.closest('[data-thumb]');
    const prev = e.target.closest('[data-gallery-prev]');
    const next = e.target.closest('[data-gallery-next]');
    if (!thumb && !prev && !next) return;
    e.stopPropagation();
    if (thumb) goTo(gallery, parseInt(thumb.dataset.thumb, 10));
    else goTo(gallery, current(gallery) + (next ? 1 : -1));
  }, true);

  const follow = (strip, thumb) => {
    const left = thumb.offsetLeft - (strip.clientWidth - thumb.offsetWidth) / 2;
    strip.scrollTo({ left: Math.max(0, left), behavior: 'smooth' });
  };
  const init = () => {
    document.querySelectorAll('.pdp-gallery__thumbs').forEach((strip) => {
      if (strip.dataset.follow) return;
      strip.dataset.follow = '1';
      new MutationObserver((changes) => changes.forEach((c) => {
        if (c.target.classList.contains('is-active')) follow(strip, c.target);
      })).observe(strip, { subtree: true, attributes: true, attributeFilter: ['class'] });
    });
    document.querySelectorAll('[data-gallery] img[loading="lazy"]').forEach((img) => { img.loading = 'eager'; });
  };
  if (document.readyState === 'complete') init(); else window.addEventListener('load', init);
  document.addEventListener('shopify:section:load', init);
})();